import { useEffect, useRef, useState } from "react";
import { Card, Col, Row, Form, Button, InputGroup } from "react-bootstrap";
import TextInput from "../../components/common/FormFields/TextInput.jsx";
import { Formik } from "formik";
import * as Yup from "yup";
import { 
  getMailerMetas, 
  addMailerMeta, 
  updateMailerMeta,
} from "../../../services/MailerService.js";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from '../common/Toast/toast';
import { useAuth } from "../../../context/AuthContext";
import { Eye, EyeOff } from 'lucide-react';
import AsyncSelectDropdown from "../common/CustomAsyncDropdown.jsx";
import "../common/SettingsStepForm.css";

/* ── The two steps ──────────────────────────────────────────────────
   Thirteen fields, and they answer two different questions: where the
   mail goes out through, and who it goes out as. On one screen that was
   a wall; split, each half is short enough to read.

   The four OAuth fields are optional and only some providers use them,
   so they fold away inside step two rather than taking a step of their
   own -- a step that is usually skipped reads as a mistake.
   ------------------------------------------------------------------ */
const STEP_COPY = [
  {
    title: "Which server sends the mail?",
    hint: "The provider, the host it connects to, and how.",
  },
  {
    title: "Who does it sign in and send as?",
    hint: "The credentials for that server, and the name recipients see.",
  },
];

const STEP_LABELS = ["Server", "Sending"];

const WarnIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5M12 16.5v.01" />
  </svg>
);

const CaretIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// Renders as a page on /add-mailer-metadata and as the body of a dialog
// when ListMailerMetas opens it. With none of these props it reads the
// URL and navigates on finish, exactly as the route always did.
function AddMailerMeta({
  // Shown inside a dialog rather than as a page: the component drops its
  // own back-link and page chrome, since the dialog supplies them.
  embedded = false,
  // The record to edit. Falls back to the :id route param.
  recordId,
  // Called after a successful save instead of navigating away.
  onDone,
  // Called when the form is dismissed instead of navigating away.
  onCancel,
  // Told the current step's question, so the dialog can title itself.
  onStepChange,
}) {
  const { id: routeId } = useParams();
  const id = recordId ?? routeId;
  const [mailerData, setMailerData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    authPass: false,
    clientSecret: false
  });
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  // Leaving the form means "go back to the list" on the route, and "close
  // the dialog" when embedded. Every exit goes through these two so the
  // component never has to know which it is.
  const leave = () => {
    if (onCancel) onCancel();
    else navigate("/mailer-metadatas");
  };

  const finish = () => {
    if (onDone) onDone();
    else navigate("/mailer-metadatas");
  };

  /* ── Steps ────────────────────────────────────────────────────────
     `step` is the one being asked for; `shownStep` is the one on
     screen, which trails it by one exit. Keying the stage on `step`
     alone swaps the content in a single frame -- the old step vanishes
     with no exit while the new one animates in from nothing, which is
     what reads as a jump. Same pair AddAttendance keeps.
     ---------------------------------------------------------------- */
  const [step, setStep] = useState(1);
  const [shownStep, setShownStep] = useState(1);
  const [stepPhase, setStepPhase] = useState("in");

  useEffect(() => {
    if (shownStep === step) return undefined;
    setStepPhase("out");
    const t = setTimeout(() => {
      setShownStep(step);
      setStepPhase("in");
    }, 160);
    return () => clearTimeout(t);
  }, [step, shownStep]);

  useEffect(() => {
    if (onStepChange) onStepChange(STEP_COPY[step - 1], step);
    // onStepChange is the dialog's setState; adding it here would
    // re-report on every render of the list behind it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Provider options
  const providerOptions = [
    { value: "google", label: "Google" },
    { value: "outlook", label: "Outlook" },
    { value: "smtp", label: "SMTP" },
    { value: "sendgrid", label: "SendGrid" },
    { value: "mailgun", label: "Mailgun" }
  ];

  // Secure options
  const secureOptions = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" }
  ];

  useEffect(() => {
    // Check permissions for both add and edit
    const permission = id ? "mailer-metadatas" : "mailer-metadatas";
    const action = id ? "update" : "add";
    
    if (!hasPermission(permission, action, "lms")) {
      if (!hasShownToast.current) {
        toast.error(`You do not have permission to ${id ? 'edit' : 'add'} mailer configurations.`);
        leave();
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate, id]);

  useEffect(() => {
    if (id) {
      fetchMailerDetails(id);
    } else {
      setMailerData({
        name: "",
        provider: "",
        host: "",
        port: "",
        secure: "true",
        authUser: "",
        authPass: "",
        fromName: "",
        fromEmail: "",
        clientId: "",
        clientSecret: "",
        refreshToken: "",
        redirectUrl: "",
        isDeleted: false
      });
      setIsEditing(false);
    }
  }, [id]);

  const fetchMailerDetails = async (mailerId) => {
    try {
      setLoading(true);
      const response = await getMailerMetas(5000);
      
      // Handle API response structure
      let mailers = [];
      if (response && response.data && Array.isArray(response.data)) {
        mailers = response.data;
      } else if (response && Array.isArray(response)) {
        mailers = response;
      }
      
      const selectedMailer = mailers.find(
        (mailer) => mailer.id?.toString() === mailerId
      );
      
      if (selectedMailer) {
        // Convert API data to form format
        const formattedMailer = {
          ...selectedMailer,
          // Convert boolean to string for form
          secure: selectedMailer.secure === true ? "true" : "false",
          // Convert number to string for form
          port: selectedMailer.port ? selectedMailer.port.toString() : "",
        };
        
        setMailerData(formattedMailer);
        setIsEditing(true);
      } else {
        toast.error("Mailer configuration not found!");
        leave();
      }
    } catch (error) {
      console.error("Error fetching mailer details:", error);
      toast.error("Error loading mailer configuration!");
      leave();
    } finally {
      setLoading(false);
    }
  };

  /* Step one's rules on their own, and the whole form's built from
     them, so "may Continue?" is answered by the same rules that decide
     "may Save?" -- a hand-written copy of them on the button is a
     second, quieter schema that drifts the moment a rule changes. */
  const stepOneSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, "Name must be at least 3 characters")
      .max(100, "Name must be less than 100 characters")
      .required("Name is required"),
    provider: Yup.string().required("Provider is required"),
    host: Yup.string()
      .min(5, "Host must be at least 5 characters")
      .required("Host is required"),
    port: Yup.string()
      .matches(/^\d+$/, "Port must be a number")
      .test('port-range', 'Port must be between 1 and 65535', function(value) {
        if (!value) return false;
        const port = parseInt(value);
        return port >= 1 && port <= 65535;
      })
      .required("Port is required"),
    secure: Yup.string().required("Secure setting is required"),
  });

  const validationSchema = stepOneSchema.shape({
    authUser: Yup.string()
      .email("Invalid email format")
      .required("Auth User is required"),
    authPass: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Auth Password is required"),
    fromName: Yup.string()
      .min(3, "From Name must be at least 3 characters")
      .required("From Name is required"),
    fromEmail: Yup.string()
      .email("Invalid email format")
      .required("From Email is required"),
    clientId: Yup.string(),
    clientSecret: Yup.string(),
    refreshToken: Yup.string(),
    redirectUrl: Yup.string().url("Invalid URL format").nullable(),
  });

  const getInitialValues = () => {
    if (mailerData) {
      return {
        name: mailerData.name || "",
        provider: mailerData.provider || "",
        host: mailerData.host || "",
        port: mailerData.port || "",
        secure: mailerData.secure || "true",
        authUser: mailerData.authUser || "",
        authPass: mailerData.authPass || "",
        fromName: mailerData.fromName || "",
        fromEmail: mailerData.fromEmail || "",
        clientId: mailerData.clientId || "",
        clientSecret: mailerData.clientSecret || "",
        refreshToken: mailerData.refreshToken || "",
        redirectUrl: mailerData.redirectUrl || "",
        isDeleted: mailerData.isDeleted || false
      };
    }
    
    return {
      name: "",
      provider: "",
      host: "",
      port: "",
      secure: "true",
      authUser: "",
      authPass: "",
      fromName: "",
      fromEmail: "",
      clientId: "",
      clientSecret: "",
      refreshToken: "",
      redirectUrl: "",
      isDeleted: false
    };
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      
      // Prepare payload with correct data types
      const payload = {
        name: values.name.trim(),
        provider: values.provider,
        host: values.host.trim(),
        port: parseInt(values.port), // Convert to number for API
        secure: values.secure === "true", // Convert to boolean for API
        authUser: values.authUser.trim(),
        authPass: values.authPass,
        fromName: values.fromName.trim(),
        fromEmail: values.fromEmail.trim(),
        clientId: values.clientId?.trim() || "",
        clientSecret: values.clientSecret || "",
        refreshToken: values.refreshToken?.trim() || "",
        redirectUrl: values.redirectUrl?.trim() || "",
        isDeleted: false
      };

      // console.log("Submission Payload:", payload);
      // console.log("Is Editing:", isEditing);
      // console.log("Mailer ID:", id);

      if (isEditing) {
        await updateMailerMeta(payload, id);
        toast.success("Mailer configuration updated successfully!");
      } else {
        await addMailerMeta(payload);
        toast.success("Mailer configuration created successfully!");
      }
      
      finish();
    } catch (error) {
      console.error("Submission error:", error);
      
      // Handle different error types
      let errorMessage = `Failed to ${isEditing ? "update" : "create"} mailer configuration.`;
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid data provided. Please check all fields.";
      } else if (error.response?.status === 401) {
        errorMessage = "You are not authorized to perform this action.";
      } else if (error.response?.status === 409) {
        errorMessage = "A configuration with this name already exists.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading state
  if (loading || (!mailerData && id)) {
    // The page's spinner is a card inside a container-fluid, which in a
    // dialog is a card inside a card. The dialog gets the bare one.
    if (embedded) {
      return (
        <div className="sf">
          <div className="sf-empty">Loading configuration…</div>
        </div>
      );
    }

    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border "
                style={{ color: "#EA580C" }}
                role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading mailer configuration...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── The dialog build ───────────────────────────────────────────────
     Two steps, the shape AddAttendance uses and the settings dialogs
     share. The stepper, the stage and its transition are the same
     `att-*` classes; everything inside a step is the `sf-*` language in
     common/SettingsStepForm.css.
     ------------------------------------------------------------------ */
  if (embedded) {
    return (
      <div className="sf">
        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched, submitForm, isSubmitting }) => {
            const bad = (name) => touched[name] && errors[name];

            const stepOneFields = ["name", "provider", "host", "port", "secure"];
            // Step one is answered when its own fields pass their own
            // rules -- the same object the full schema is built from, so
            // there is no second copy of them here to fall out of step.
            const stepOneOk = stepOneSchema.isValidSync(values);

            const goToTwo = () => {
              stepOneFields.forEach((n) => setFieldTouched(n, true, true));
              if (stepOneOk) setStep(2);
            };

            const save = () => {
              ["authUser", "authPass", "fromName", "fromEmail"].forEach((n) =>
                setFieldTouched(n, true, true)
              );
              // Saving with a step-one field invalid would fail validation
              // silently -- its error lives on a step that is not on
              // screen. Go back and show it.
              if (!stepOneOk) {
                setStep(1);
                return;
              }
              submitForm();
            };

            const field = (name, label, extra = {}) => (
              <div className="sf-field">
                <div className="sf-label-row">
                  <label className="sf-label" htmlFor={`mm-${name}`}>
                    {label}
                    {extra.required !== false && <span className="sf-req">*</span>}
                  </label>
                </div>
                {extra.reveal ? (
                  <div className="sf-input-wrap">
                    <input
                      id={`mm-${name}`}
                      name={name}
                      type={showPasswords[name] ? "text" : "password"}
                      className={`sf-input${bad(name) ? " sf-input--bad" : ""}`}
                      value={values[name] || ""}
                      placeholder={extra.placeholder}
                      autoComplete="new-password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <button
                      type="button"
                      className="sf-reveal"
                      onClick={() => togglePasswordVisibility(name)}
                      aria-label={showPasswords[name] ? "Hide" : "Show"}
                    >
                      {showPasswords[name] ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                ) : (
                  <input
                    id={`mm-${name}`}
                    name={name}
                    type={extra.type || "text"}
                    className={`sf-input${bad(name) ? " sf-input--bad" : ""}`}
                    value={values[name] || ""}
                    placeholder={extra.placeholder}
                    inputMode={extra.inputMode}
                    autoComplete="off"
                    spellCheck={extra.spellCheck}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                )}
                {bad(name) ? (
                  <p className="sf-error"><WarnIcon />{errors[name]}</p>
                ) : (
                  extra.hint && <p className="sf-hint">{extra.hint}</p>
                )}
              </div>
            );

            return (
              <Form
                noValidate
                onSubmit={(e) => {
                  // Nothing in here is type="submit", so the only thing
                  // that reaches this is Enter pressed inside a field. On
                  // step one that key means "continue".
                  e.preventDefault();
                  if (step !== 2) {
                    goToTwo();
                    return;
                  }
                  save();
                }}
              >
                <ol className="att-steps">
                  {STEP_LABELS.map((label, i) => {
                    const n = i + 1;
                    const state = n === step ? "is-now" : n < step ? "is-done" : "";
                    return (
                      <li className={`att-step-tab ${state}`.trim()} key={label}>
                        <button
                          type="button"
                          // Adding: only backwards, a step ahead has not been
                          // answered yet. Editing: every step is filled, so any tab opens.
                          disabled={n === step || (!isEditing && n > step)}
                          onClick={() => setStep(n)}
                        >
                          <span className="att-step-n">{n}</span>
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </ol>

                {/* Painted from shownStep, not step -- see the comment on
                    the transition effect above. */}
                <div className={`att-stage is-${stepPhase}`}>
                  <div className="att-stage-inner" key={shownStep}>
                    {shownStep === 1 && (
                      <section className="att-step">
                        {field("name", "Name", {
                          placeholder: "Transactional mail, Newsletter…",
                          hint: "What you will recognise this configuration by.",
                        })}

                        <div className="sf-field">
                          <div className="sf-label-row">
                            <label className="sf-label" htmlFor="mm-provider">
                              Provider<span className="sf-req">*</span>
                            </label>
                          </div>
                          <select
                            id="mm-provider"
                            className={`sf-input${bad("provider") ? " sf-input--bad" : ""}`}
                            value={values.provider}
                            onChange={(e) => setFieldValue("provider", e.target.value)}
                            onBlur={() => setFieldTouched("provider", true, true)}
                          >
                            <option value="">Select a provider…</option>
                            {providerOptions.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          {bad("provider") ? (
                            <p className="sf-error"><WarnIcon />{errors.provider}</p>
                          ) : (
                            <p className="sf-hint">
                              Google and Outlook use the OAuth fields on the next step.
                            </p>
                          )}
                        </div>

                        <div className="sf-row">
                          {field("host", "Host", {
                            placeholder: "smtp.gmail.com",
                            spellCheck: false,
                          })}
                          {field("port", "Port", {
                            inputMode: "numeric",
                            placeholder: "587",
                            hint: "465 for SSL, 587 for STARTTLS.",
                          })}
                        </div>

                        <div className="sf-field">
                          <div className="sf-label-row">
                            <label className="sf-label">
                              Secure<span className="sf-req">*</span>
                            </label>
                          </div>
                          {/* Two choices shown as themselves rather than
                              hidden behind a select. */}
                          <div className="sf-seg" role="group" aria-label="Secure">
                            {secureOptions.map((o) => (
                              <button
                                key={o.value}
                                type="button"
                                className={`sf-seg-opt${String(values.secure) === o.value ? " is-on" : ""}`}
                                aria-pressed={String(values.secure) === o.value}
                                onClick={() => {
                                  setFieldTouched("secure", true, false);
                                  setFieldValue("secure", o.value);
                                }}
                              >
                                {o.label}
                              </button>
                            ))}
                          </div>
                          {bad("secure") ? (
                            <p className="sf-error"><WarnIcon />{errors.secure}</p>
                          ) : (
                            <p className="sf-hint">
                              Yes for an implicit TLS port such as 465.
                            </p>
                          )}
                        </div>
                      </section>
                    )}

                    {shownStep === 2 && (
                      <section className="att-step">
                        <div className="sf-row">
                          {field("authUser", "Auth user", {
                            type: "email",
                            placeholder: "mailer@example.com",
                            hint: "The account that signs in to the server.",
                          })}
                          {field("authPass", "Auth password", {
                            reveal: true,
                            placeholder: "••••••••",
                          })}
                        </div>

                        <div className="sf-row">
                          {field("fromName", "From name", {
                            placeholder: "SevenMentor",
                            hint: "The name recipients see.",
                          })}
                          {field("fromEmail", "From email", {
                            type: "email",
                            placeholder: "noreply@example.com",
                          })}
                        </div>

                        {/* Optional, and only some providers use them --
                            folded away so the common path stays short. */}
                        <details className="sf-more">
                          <summary>
                            <CaretIcon />
                            OAuth 2.0
                            <span className="sf-more-note">
                              optional — Google and Outlook
                            </span>
                          </summary>
                          <div className="sf-more-body">
                            <div className="sf-row">
                              {field("clientId", "Client ID", {
                                required: false,
                                spellCheck: false,
                              })}
                              {field("clientSecret", "Client secret", {
                                required: false,
                                reveal: true,
                              })}
                            </div>
                            <div className="sf-row">
                              {field("refreshToken", "Refresh token", {
                                required: false,
                                spellCheck: false,
                              })}
                              {field("redirectUrl", "Redirect URL", {
                                required: false,
                                spellCheck: false,
                                placeholder: "https://…",
                              })}
                            </div>
                          </div>
                        </details>
                      </section>
                    )}
                  </div>
                </div>

                <footer className="sf-foot">
                  {/* Keyed, and neither is type="submit". Two buttons at one
                      position are otherwise reconciled onto a single DOM
                      node: clicking Continue would flush step=2 before the
                      browser reached the click's default action, by which
                      time that same node was a submit button inside a form
                      -- and the record saved from step one. */}
                  {step > 1 ? (
                    <button key="back" type="button" className="sf-btn" onClick={() => setStep(1)}>
                      Back
                    </button>
                  ) : (
                    <button key="cancel" type="button" className="sf-btn" onClick={leave}>
                      Cancel
                    </button>
                  )}

                  <span className="sf-foot-gap" />

                  {step === 1 ? (
                    <button
                      key="continue"
                      type="button"
                      className="sf-btn sf-btn--main"
                      onClick={goToTwo}
                      disabled={!stepOneOk}
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      key="save"
                      type="button"
                      className="sf-btn sf-btn--main"
                      disabled={isSubmitting}
                      onClick={save}
                    >
                      {isSubmitting && <span className="sf-spin" aria-hidden="true" />}
                      {isSubmitting
                        ? isEditing ? "Updating…" : "Saving…"
                        : isEditing ? "Update configuration" : "Create configuration"}
                    </button>
                  )}
                </footer>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">
                  {isEditing ? "Edit Mailer Configuration" : "Add New Mailer Configuration"}
                </h4>
                <small className="text-muted">
                  {isEditing ? "Update the email server configuration" : "Create a new email server configuration"}
                </small>
              </div>
              <Link to="/mailer-metadatas" className="btn btn-secondary">
                <i className="fa fa-arrow-left me-2"></i>
                Back to List
              </Link>
            </Card.Header>
            
            <Card.Body>
              <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldValue,
                  isSubmitting,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      {/* Configuration Information Section */}
                      <Col md={12}>
                        <h5 className="mb-3 "
                        style={{ color: "#EA580C" }}
                        >
                          <i className="fa fa-info-circle me-2"></i>
                          Configuration Information
                        </h5>
                      </Col>

                      {/* Basic Information */}
                      <Col md={6}>
                        <TextInput
                          label="Configuration Name"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.name}
                          touched={touched.name}
                          placeholder="e.g., Billing SMTP OAuth"
                          required
                        />
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-dark">Provider <span className="text-danger">*</span></Form.Label>
                          <AsyncSelectDropdown
                            value={providerOptions.find(option => option.value === values.provider) || null}
                            onChange={(selectedOption) => setFieldValue("provider", selectedOption?.value || "")}
                            options={providerOptions}
                            placeholder="Select Provider..."
                            isClearable
                          />
                          {errors.provider && touched.provider && (
                            <Form.Text className="text-danger">{errors.provider}</Form.Text>
                          )}
                        </Form.Group>
                      </Col>

                      {/* Server Configuration Section */}
                      <Col md={12}>
                        <h5 className="mb-3 mt-4 "
                        style={{ color: "#EA580C" }}
                        >
                          <i className="fa fa-server me-2"></i>
                          Server Configuration
                        </h5>
                      </Col>

                      {/* Server Configuration */}
                      <Col md={6}>
                        <TextInput
                          label="Host"
                          name="host"
                          value={values.host}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.host}
                          touched={touched.host}
                          placeholder="e.g., smtp.gmail.com"
                          required
                        />
                      </Col>
                      
                      <Col md={3}>
                        <TextInput
                          label="Port"
                          name="port"
                          type="number"
                          value={values.port}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.port}
                          touched={touched.port}
                          placeholder="e.g., 465"
                          required
                        />
                      </Col>

                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-dark">Secure Connection <span className="text-danger">*</span></Form.Label>
                          <AsyncSelectDropdown
                            value={secureOptions.find(option => option.value === values.secure) || null}
                            onChange={(selectedOption) => setFieldValue("secure", selectedOption?.value || "true")}
                            options={secureOptions}
                            placeholder="Select..."
                          />
                          {errors.secure && touched.secure && (
                            <Form.Text className="text-danger">{errors.secure}</Form.Text>
                          )}
                        </Form.Group>
                      </Col>

                      {/* Authentication Section */}
                      <Col md={12}>
                        <h5 className="mb-3 mt-4 "
                        style={{ color: "#EA580C" }}
                        >
                          <i className="fa fa-lock me-2"></i>
                          Authentication
                        </h5>
                      </Col>

                      {/* Authentication */}
                      <Col md={6}>
                        <TextInput
                          label="Auth User (Email)"
                          name="authUser"
                          type="email"
                          value={values.authUser}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.authUser}
                          touched={touched.authUser}
                          placeholder="e.g., billing@sevenmentor.com"
                          required
                        />
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Auth Password <span className="text-danger">*</span></Form.Label>
                          <InputGroup>
                            <Form.Control
                              type={showPasswords.authPass ? "text" : "password"}
                              name="authPass"
                              value={values.authPass}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter password or token"
                              isInvalid={errors.authPass && touched.authPass}
                            />
                            <Button
                              variant="outline-secondary"
                              onClick={() => togglePasswordVisibility('authPass')}
                              style={{ border: '1px solid #ced4da', borderLeft: 'none' }}
                            >
                              {showPasswords.authPass ? <EyeOff size={16} /> : <Eye size={16} />}
                            </Button>
                          </InputGroup>
                          {errors.authPass && touched.authPass && (
                            <Form.Text className="text-danger">{errors.authPass}</Form.Text>
                          )}
                        </Form.Group>
                      </Col>

                      {/* From Information Section */}
                      <Col md={12}>
                        <h5 className="mb-3 mt-4 "
                        style={{ color: "#EA580C" }}
                        >
                          <i className="fa fa-envelope me-2"></i>
                          From Information
                        </h5>
                      </Col>

                      {/* From Information */}
                      <Col md={6}>
                        <TextInput
                          label="From Name"
                          name="fromName"
                          value={values.fromName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.fromName}
                          touched={touched.fromName}
                          placeholder="e.g., SevenMentor & Training"
                          required
                        />
                      </Col>
                      
                      <Col md={6}>
                        <TextInput
                          label="From Email"
                          name="fromEmail"
                          type="email"
                          value={values.fromEmail}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.fromEmail}
                          touched={touched.fromEmail}
                          placeholder="e.g., billing@sevenmentor.com"
                          required
                        />
                      </Col>

                      {/* OAuth Settings Section */}
                      <Col md={12}>
                        <h5 className="mb-3 mt-4 "
                        style={{ color: "#EA580C" }}
                        >
                          <i className="fa fa-key me-2"></i>
                          OAuth Settings (Optional)
                        </h5>
                      </Col>

                      {/* OAuth Settings */}
                      <Col md={6}>
                        <TextInput
                          label="Client ID (Optional)"
                          name="clientId"
                          value={values.clientId}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.clientId}
                          touched={touched.clientId}
                          placeholder="OAuth Client ID"
                        />
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Client Secret (Optional)</Form.Label>
                          <InputGroup>
                            <Form.Control
                              type={showPasswords.clientSecret ? "text" : "password"}
                              name="clientSecret"
                              value={values.clientSecret}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="OAuth Client Secret"
                              isInvalid={errors.clientSecret && touched.clientSecret}
                            />
                            <Button
                              variant="outline-secondary"
                              onClick={() => togglePasswordVisibility('clientSecret')}
                              style={{ border: '1px solid #ced4da', borderLeft: 'none' }}
                            >
                              {showPasswords.clientSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                            </Button>
                          </InputGroup>
                          {errors.clientSecret && touched.clientSecret && (
                            <Form.Text className="text-danger">{errors.clientSecret}</Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <TextInput
                          label="Refresh Token (Optional)"
                          name="refreshToken"
                          value={values.refreshToken}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.refreshToken}
                          touched={touched.refreshToken}
                          placeholder="OAuth Refresh Token"
                        />
                      </Col>
                      
                      <Col md={6}>
                        <TextInput
                          label="Redirect URL (Optional)"
                          name="redirectUrl"
                          value={values.redirectUrl}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.redirectUrl}
                          touched={touched.redirectUrl}
                          placeholder="OAuth Redirect URL"
                        />
                      </Col>
                    </Row>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                      <Link to="/mailer-metadatas" className="btn btn-outline-secondary">
                        <i className="fa fa-times me-2"></i>
                        Cancel
                      </Link>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {isEditing ? "Updating..." : "Creating..."}
                          </>
                        ) : (
                          <>
                            <i className={`fa ${isEditing ? 'fa-save' : 'fa-plus'} me-2`}></i>
                            {isEditing ? "Update Configuration" : "Create Configuration"}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AddMailerMeta;
